/**
 * Where to render a result that extracted nothing.
 *
 * A result with no fields has no chunk to attribute and collapses to a single
 * anchor section, so the anchor is the only thing telling the operator which
 * page the run was about. Two consumers depend on it: the section's position in
 * the viewer, and `pageIndexInResult` — the GLOBAL page number a QA page verdict
 * and a `page_notes` entry are keyed by.
 *
 * `page_id` is NOT that page. The pipeline attaches every result of a container
 * to the workload assignment's FIRST `BatchPageRecord`, so `page_id` is the same
 * value on every row and always resolves to PG-001. A per-page template
 * assignment that gave a run page 8 still rendered its refusal under PG-001,
 * next to the unrelated run that genuinely owns page 1 — which is what badged
 * that page as a two-template pool when only one template had ever been
 * assigned to it. Keyed there, a QA verdict on the card was also out of range
 * for the result that showed it.
 *
 * `assigned_page_numbers` is the run's own answer and outranks it. `page_id`
 * survives as the fallback for rows predating the column, where it is still
 * meaningful: a `skipped` duplicate-accounting row IS created per page and
 * carries a distinct one.
 */
export interface EmptyResultAnchor {
  /** 0-based index into the result's rendered page list. */
  pageIndex: number;
  /** Global 1-based page number — the key space QA page verdicts use. */
  pageNumber: number;
}

export function resolveEmptyResultAnchor(result: {
  page_id?: number | null;
  pages: { id: number }[];
  assigned_page_numbers?: number[] | null;
}): EmptyResultAnchor {
  const { pages } = result;
  const lastIndex = Math.max(0, pages.length - 1);

  const assigned = result.assigned_page_numbers ?? [];

  if (assigned.length > 0) {
    // Lowest, not first: the column is stored sorted, but reading document
    // order off array position would put the anchor mid-document the day
    // something writes it unsorted.
    const pageNumber = Math.min(...assigned);

    // Clamped so a stale assignment cannot index past the rendered pages, while
    // `pageNumber` stays the true global number the verdict must be keyed by.
    return {
      pageIndex: Math.min(Math.max(0, pageNumber - 1), lastIndex),
      pageNumber
    };
  }

  // No assignment to go on. An empty one is an answer, not a gap — a pool
  // sibling that won no page — and both land here with nothing better than the
  // first page to point at.
  const ownIndex = pages.findIndex((p) => p.id === result.page_id);

  return {
    pageIndex: ownIndex >= 0 ? ownIndex : 0,
    // Still 1: `pageIndexInResult` addressed a page WITHIN the result, and a
    // legacy row with no extraction has exactly one.
    pageNumber: 1
  };
}
